import GitHubIcon from "@mui/icons-material/GitHub";
import { Box, Chip, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { useApiQuery } from "../../hooks/use-api-query";
import { BRAND_PRIMARY, RADIUS_LG } from "../../theme/theme";
import type { AppConfig, Test, TestSuite } from "../../types";
import type { ListArgusSchedulesResponse } from "../../types/argus";
import { ArgusEye } from "./argus-eye";

/**
 * Onboarding welcome card — replaces the sentinel-bearing first message in
 * the chat when a session has `config.is_onboarding === true`.
 *
 * Composition:
 *  - Small Argus eye illustration + intro copy
 *  - Four progress chips driven by existing react-query data:
 *      1. App configured       — `/vibe-qa/app-configs`
 *      2. First test created   — `/tests`
 *      3. Schedule set         — `/test-suites` (any suite with a non-null
 *                                `schedule` cron) OR `/client/argus/schedules`
 *                                (any active argus schedule, for backwards
 *                                compat with the pre-F22 skill path that
 *                                created ArgusSchedule rows directly).
 *      4. argus-action wired   — link to `/configuration?tab=argus`
 *
 * Chips 1-3 react to invalidations from the underlying queries (mutations
 * in the agent chat invalidate their respective caches), so the state
 * updates within ~1s of the user creating the corresponding artifact.
 * Chip 4 is an always-on link that takes the user to the GitHub-Action
 * setup docs — there's no way to detect "wired" from the app side (that
 * state lives in the user's GitHub repo), so the chip is informational.
 */

interface ProgressChipProps {
  label: string;
  filled: boolean;
  dimmed?: boolean;
  tooltip?: string;
}

const ProgressChip = ({ label, filled, dimmed, tooltip }: ProgressChipProps) => {
  const chip = (
    <Chip
      label={label}
      size="small"
      variant={filled ? "filled" : "outlined"}
      sx={{
        fontWeight: 500,
        fontSize: "0.75rem",
        borderColor: filled ? BRAND_PRIMARY : "divider",
        color: dimmed ? "text.disabled" : filled ? "common.white" : "text.secondary",
        backgroundColor: filled ? BRAND_PRIMARY : "transparent",
        opacity: dimmed ? 0.6 : 1,
      }}
    />
  );
  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow>
        <span>{chip}</span>
      </Tooltip>
    );
  }
  return chip;
};

export const ArgusWelcomeCard = () => {
  const { data: appConfigs } = useApiQuery<AppConfig[]>("/vibe-qa/app-configs");
  const { data: tests } = useApiQuery<Test[]>("/tests");
  const { data: testSuites } = useApiQuery<TestSuite[]>("/test-suites");
  const { data: argusSchedulesResponse } = useApiQuery<ListArgusSchedulesResponse>(
    "/client/argus/schedules?status=active&limit=100",
  );

  const hasAppConfig = (appConfigs?.length ?? 0) > 0;
  const hasTest = (tests?.length ?? 0) > 0;
  const hasSuiteSchedule = (testSuites ?? []).some((s) => !!s.schedule);
  const hasArgusSchedule = (argusSchedulesResponse?.schedules?.length ?? 0) > 0;
  const hasSchedule = hasSuiteSchedule || hasArgusSchedule;

  return (
    <Paper
      variant="outlined"
      sx={{
        padding: { xs: 2, sm: 3 },
        borderRadius: `${RADIUS_LG}px`,
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            flexShrink: 0,
          }}
        >
          <ArgusEye />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 0.5 }}>
            Hi, I'm Argus.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            I'll help you get your workspace ready.
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ marginTop: 2, flexWrap: "wrap", gap: 1 }} useFlexGap>
        <ProgressChip label="App configured" filled={hasAppConfig} />
        <ProgressChip label="First test created" filled={hasTest} />
        <ProgressChip label="Schedule set" filled={hasSchedule} />
        <Tooltip title="Open the GitHub Action setup" arrow>
          <Chip
            component={RouterLink}
            to="/configuration?tab=argus"
            clickable
            icon={<GitHubIcon sx={{ fontSize: 16 }} />}
            label="argus-action"
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 500,
              fontSize: "0.75rem",
              borderColor: BRAND_PRIMARY,
              color: BRAND_PRIMARY,
              "& .MuiChip-icon": { color: BRAND_PRIMARY },
              "&:hover": {
                backgroundColor: `${BRAND_PRIMARY}0D`,
              },
            }}
          />
        </Tooltip>
      </Stack>
    </Paper>
  );
};
